/**
 * Fixed-timestep scheduler (non-negotiable #2).
 *
 * Physics runs at exactly 120 Hz regardless of what the display is doing. Rendering
 * interpolates between the last two physics states using `alpha`.
 *
 * ## Why this is not the textbook accumulator
 *
 * The usual formulation is `accumulator += frameDt; while (accumulator >= dt) { step(); accumulator -= dt }`.
 * That accumulates floating-point error: at 144 fps the residual drifts by ~1e-14 per frame
 * and, over a second, silently produces 119 or 121 ticks instead of 120. A flight model that
 * is integrating an unstable spin does not appreciate a variable tick budget, and a lock-step
 * multiplayer peer appreciates it even less.
 *
 * Instead we derive the tick count from *absolute* time:
 *
 *     targetTicks = floor((now − epoch) × hz / 1000)
 *     ticksThisFrame = targetTicks − ticksAlreadyRun
 *
 * The result is drift-free by construction — the tick count is a pure function of the clock,
 * so error cannot compound — and it stays exact for days of wall-clock time (a ms timestamp
 * is an exact double well past 2^53 / 120).
 *
 * `epoch` is the wall-clock instant that tick 0 happened at. Everything that must bend time —
 * pausing, single-stepping, dropping ticks after a stall — moves the epoch rather than the
 * tick counter, which keeps `ticks` equal to the number of `updateFixed` calls actually made.
 *
 * Allocation-free: `advance()` returns a count and mutates numbers only.
 */

export interface FixedStepSchedulerOptions {
  /** Physics rate [Hz]. Defaults to the project-mandated 120. */
  hz?: number;
  /**
   * Ceiling on ticks executed in one frame. Beyond this the scheduler *drops* simulated
   * time instead of trying to catch up — the classic "spiral of death" guard. 12 ticks =
   * 100 ms of catch-up per frame, enough to ride out a GC pause without stalling the tab.
   */
  maxStepsPerFrame?: number;
}

export const DEFAULT_PHYSICS_HZ = 120;

export class FixedStepScheduler {
  /** Physics rate [Hz]. */
  readonly hz: number;
  /** Fixed timestep handed to every `updateFixed` [s]. */
  readonly stepSeconds: number;
  /** Fixed timestep [ms]. */
  readonly stepMs: number;
  /** Ceiling on ticks per frame before simulated time is dropped. */
  readonly maxStepsPerFrame: number;

  private epochMs = 0;
  private lastNowMs = 0;
  private ticks = 0;
  private alphaValue = 0;
  private pausedValue = false;
  private pendingSingleSteps = 0;
  private droppedTicksValue = 0;
  private started = false;

  constructor(options: FixedStepSchedulerOptions = {}) {
    this.hz = options.hz ?? DEFAULT_PHYSICS_HZ;
    if (!(this.hz > 0)) throw new RangeError('FixedStepScheduler: hz must be > 0');
    this.stepSeconds = 1 / this.hz;
    this.stepMs = 1000 / this.hz;
    this.maxStepsPerFrame = Math.max(1, options.maxStepsPerFrame ?? 12);
  }

  /** Anchor tick 0 to `nowMs`. Called implicitly by the first `advance()`. */
  start(nowMs: number): void {
    this.epochMs = nowMs;
    this.lastNowMs = nowMs;
    this.ticks = 0;
    this.alphaValue = 0;
    this.pendingSingleSteps = 0;
    this.droppedTicksValue = 0;
    this.started = true;
  }

  /**
   * Advance the clock to `nowMs` and report how many `updateFixed(stepSeconds)` calls the
   * caller owes the simulation. Call exactly once per rendered frame, then run that many
   * ticks, then read {@link alpha} for the render interpolation factor.
   */
  advance(nowMs: number): number {
    if (!this.started) {
      this.start(nowMs);
      return 0;
    }

    const wallDeltaMs = nowMs - this.lastNowMs;
    this.lastNowMs = nowMs;

    if (this.pausedValue) {
      // Freeze simulated time by dragging the epoch along with the wall clock. The
      // fractional part of `elapsedTicks` is preserved, so `alpha` (and therefore the
      // rendered pose) stays rock-steady while paused.
      this.epochMs += wallDeltaMs;
      if (this.pendingSingleSteps > 0) {
        const n = Math.min(this.pendingSingleSteps, this.maxStepsPerFrame);
        this.pendingSingleSteps -= n;
        this.epochMs -= n * this.stepMs;
        this.ticks += n;
        return n;
      }
      return 0;
    }

    const elapsedTicks = ((nowMs - this.epochMs) * this.hz) / 1000;
    let n = Math.floor(elapsedTicks) - this.ticks;

    if (n < 0) {
      // Clock went backwards (manual clock, tab restore). Re-anchor rather than un-tick.
      this.epochMs = nowMs - (this.ticks * this.stepMs);
      this.alphaValue = 0;
      return 0;
    }

    let dropped = 0;
    if (n > this.maxStepsPerFrame) {
      dropped = n - this.maxStepsPerFrame;
      this.epochMs += dropped * this.stepMs;
      this.droppedTicksValue += dropped;
      n = this.maxStepsPerFrame;
    }

    this.ticks += n;
    const alpha = elapsedTicks - dropped - this.ticks;
    this.alphaValue = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    return n;
  }

  /**
   * Render interpolation factor in [0, 1): how far the render time sits between the
   * previous physics state and the current one. Valid after `advance()`.
   */
  get alpha(): number {
    return this.alphaValue;
  }

  /** Total physics ticks executed since `start()`. */
  get totalTicks(): number {
    return this.ticks;
  }

  /** Simulated time since `start()` [s]. Exactly `totalTicks × stepSeconds`. */
  get simTimeSeconds(): number {
    return this.ticks * this.stepSeconds;
  }

  /** Ticks abandoned by the spiral-of-death guard. Non-zero means the frame time blew up. */
  get droppedTicks(): number {
    return this.droppedTicksValue;
  }

  get paused(): boolean {
    return this.pausedValue;
  }

  setPaused(paused: boolean): void {
    this.pausedValue = paused;
    if (!paused) this.pendingSingleSteps = 0;
  }

  pause(): void {
    this.setPaused(true);
  }

  resume(): void {
    this.setPaused(false);
  }

  /**
   * Queue `count` physics ticks to run on the next `advance()` while paused — frame-by-frame
   * debugging of the flight model. No-op when running.
   */
  requestSingleStep(count = 1): void {
    if (this.pausedValue) this.pendingSingleSteps += count;
  }

  /** Ticks queued by {@link requestSingleStep} and not yet executed. */
  get pendingStepCount(): number {
    return this.pendingSingleSteps;
  }
}
